/**
 * Create Integration Dialog
 * 
 * Dialog for creating new integrations with type-specific forms
 */

import React, { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { integrationUtils } from '@/features/integrations/utils/integrationUtils'
import { IntegrationType } from '@/types/integrations'
import { JobberIntegrationForm } from './forms/JobberIntegrationForm'
import { GMBIntegrationForm } from './forms/GMBIntegrationForm'
import { AnalyticsIntegrationForm } from './forms/AnalyticsIntegrationForm'
import { WordPressIntegrationForm } from './forms/WordPressIntegrationForm'

interface CreateIntegrationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedType?: IntegrationType | null
  onSuccess?: () => void
}

const integrationTypes: Array<{
  type: IntegrationType
  category: 'crm' | 'marketing' | 'content'
  difficulty: 'easy' | 'medium' | 'advanced'
  setupTime: string
}> = [
  {
    type: 'jobber_crm',
    category: 'crm',
    difficulty: 'medium',
    setupTime: '10-15 min',
  },
  {
    type: 'google_my_business',
    category: 'marketing',
    difficulty: 'medium',
    setupTime: '5-10 min',
  },
  {
    type: 'google_analytics',
    category: 'marketing',
    difficulty: 'easy',
    setupTime: '3-5 min',
  },
  {
    type: 'google_search_console',
    category: 'marketing',
    difficulty: 'easy',
    setupTime: '3-5 min',
  },
  {
    type: 'facebook_pixel',
    category: 'marketing',
    difficulty: 'easy',
    setupTime: '2-3 min',
  },
  {
    type: 'wordpress',
    category: 'content',
    difficulty: 'advanced',
    setupTime: '15-30 min',
  },
]

export function CreateIntegrationDialog({
  open,
  onOpenChange,
  selectedType,
  onSuccess,
}: CreateIntegrationDialogProps) {
  const [currentStep, setCurrentStep] = useState<'select' | 'configure'>('select')
  const [chosenType, setChosenType] = useState<IntegrationType | null>(selectedType || null)

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      if (selectedType) {
        setChosenType(selectedType)
        setCurrentStep('configure')
      } else {
        setChosenType(null)
        setCurrentStep('select')
      }
    }
  }, [open, selectedType])

  const handleTypeSelect = (type: IntegrationType) => {
    setChosenType(type)
    setCurrentStep('configure')
  }

  const handleBack = () => {
    setCurrentStep('select')
    setChosenType(null)
  }

  const handleSuccess = () => {
    onSuccess?.()
    onOpenChange(false)
    setCurrentStep('select')
    setChosenType(null)
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-800'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800'
      case 'advanced':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const renderIntegrationForm = () => {
    if (!chosenType) return null

    switch (chosenType) {
      case 'jobber_crm':
        return <JobberIntegrationForm onSuccess={handleSuccess} onCancel={handleBack} />
      case 'google_my_business':
        return <GMBIntegrationForm onSuccess={handleSuccess} onCancel={handleBack} />
      case 'google_analytics':
      case 'google_search_console':
      case 'facebook_pixel':
        return (
          <AnalyticsIntegrationForm
            type={chosenType}
            onSuccess={handleSuccess}
            onCancel={handleBack}
          />
        )
      case 'wordpress':
        return <WordPressIntegrationForm onSuccess={handleSuccess} onCancel={handleBack} />
      default:
        return null
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        {currentStep === 'select' ? (
          <>
            <DialogHeader>
              <DialogTitle>Add New Integration</DialogTitle>
              <DialogDescription>
                Choose an integration type to connect external services with your admin dashboard
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 mt-6">
              {/* CRM & Customer Data */}
              <div>
                <h3 className="text-lg font-semibold mb-3">CRM & Customer Data</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {integrationTypes
                    .filter(integration => integration.category === 'crm')
                    .map((integration) => (
                      <Card
                        key={integration.type}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleTypeSelect(integration.type)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="text-2xl">
                                {integrationUtils.typeIcons[integration.type]}
                              </div>
                              <div>
                                <CardTitle className="text-base">
                                  {integrationUtils.typeLabels[integration.type]}
                                </CardTitle>
                                <CardDescription className="text-sm">
                                  {integrationUtils.typeDescriptions[integration.type]}
                                </CardDescription>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="secondary"
                              className={getDifficultyColor(integration.difficulty)}
                            >
                              {integration.difficulty}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {integration.setupTime} setup
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>

              {/* Marketing & Analytics */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Marketing & Analytics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {integrationTypes
                    .filter(integration => integration.category === 'marketing')
                    .map((integration) => (
                      <Card
                        key={integration.type}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleTypeSelect(integration.type)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="text-2xl">
                                {integrationUtils.typeIcons[integration.type]}
                              </div>
                              <div>
                                <CardTitle className="text-base">
                                  {integrationUtils.typeLabels[integration.type]}
                                </CardTitle>
                                <CardDescription className="text-sm">
                                  {integrationUtils.typeDescriptions[integration.type]}
                                </CardDescription>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="secondary"
                              className={getDifficultyColor(integration.difficulty)}
                            >
                              {integration.difficulty}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {integration.setupTime} setup
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>

              {/* Content & Migration */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Content & Migration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {integrationTypes
                    .filter(integration => integration.category === 'content')
                    .map((integration) => (
                      <Card
                        key={integration.type}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleTypeSelect(integration.type)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="text-2xl">
                                {integrationUtils.typeIcons[integration.type]}
                              </div>
                              <div>
                                <CardTitle className="text-base">
                                  {integrationUtils.typeLabels[integration.type]}
                                </CardTitle>
                                <CardDescription className="text-sm">
                                  {integrationUtils.typeDescriptions[integration.type]}
                                </CardDescription>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="secondary"
                              className={getDifficultyColor(integration.difficulty)}
                            >
                              {integration.difficulty}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {integration.setupTime} setup
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="text-2xl">
                  {chosenType && integrationUtils.typeIcons[chosenType]}
                </div>
                <div>
                  <DialogTitle>
                    Configure {chosenType && integrationUtils.typeLabels[chosenType]}
                  </DialogTitle>
                  <DialogDescription>
                    {chosenType && integrationUtils.typeDescriptions[chosenType]}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            
            <div className="mt-6">
              {renderIntegrationForm()}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
